/**
 * Shopify Customer Privacy API integration for headless storefronts.
 * Loads the consent-tracking-api script and provides helpers to
 * read / write consent state on Shopify-managed surfaces (pixels, checkout, audiences).
 */

const SHOPIFY_STORE_DOMAIN = "motive-structure-go3xe.myshopify.com";
const PRIVACY_SCRIPT_URL =
  "https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js";

/* ── types ── */
export type ConsentCategory = "analytics" | "marketing" | "preferences";

export interface ConsentDecision {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

/* Shopify's window augmentation */
declare global {
  interface Window {
    Shopify?: {
      customerPrivacy?: {
        setTrackingConsent: (
          consent: {
            analytics: boolean;
            marketing: boolean;
            preferences: boolean;
            sale_of_data: boolean;
          },
          callback: () => void
        ) => void;
        currentVisitorConsent: () => {
          analytics: string; // "yes" | "no" | ""
          marketing: string;
          preferences: string;
          sale_of_data: string;
        };
        shouldShowBanner: () => boolean;
        userCanBeTracked: () => boolean;
        getTrackingConsent: () => string;
      };
    };
  }
}

/* ── script loader (idempotent) ── */
let loadPromise: Promise<void> | null = null;

export function loadShopifyPrivacyApi(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve) => {
    // Already present (e.g. if Shopify injected it)
    if (window.Shopify?.customerPrivacy) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = PRIVACY_SCRIPT_URL;
    script.async = true;
    script.dataset.shopifyDomain = SHOPIFY_STORE_DOMAIN;

    script.onload = () => {
      // Give Shopify a tick to initialise the object
      const poll = setInterval(() => {
        if (window.Shopify?.customerPrivacy) {
          clearInterval(poll);
          resolve();
        }
      }, 50);
      // Safety: resolve anyway after 3 s so the banner still works
      setTimeout(() => {
        clearInterval(poll);
        resolve();
      }, 3000);
    };

    script.onerror = () => {
      console.warn("[shopify-privacy] Failed to load consent-tracking-api script – falling back to local-only consent.");
      resolve(); // non-blocking: banner still works locally
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/* ── sync helpers ── */

/**
 * Push a consent decision to Shopify's Customer Privacy API.
 * If the API isn't available (script failed to load), this is a no-op.
 */
export function syncConsentToShopify(decision: ConsentDecision): void {
  const api = window.Shopify?.customerPrivacy;
  if (!api) {
    console.warn("[shopify-privacy] Customer Privacy API not available – consent stored locally only.");
    return;
  }

  try {
    api.setTrackingConsent(
      {
        analytics: decision.analytics,
        marketing: decision.marketing,
        preferences: decision.preferences,
        sale_of_data: !decision.marketing, // opt-out when marketing is denied
      },
      () => {
        console.info("[shopify-privacy] Consent synced to Shopify:", decision);
      }
    );
  } catch (err) {
    console.error("[shopify-privacy] Failed to sync consent:", err);
  }
}

/**
 * Read the current visitor consent from Shopify (if available).
 */
export function readShopifyConsent(): ConsentDecision | null {
  const api = window.Shopify?.customerPrivacy;
  if (!api) return null;

  try {
    const raw = api.currentVisitorConsent();
    return {
      analytics: raw.analytics === "yes",
      marketing: raw.marketing === "yes",
      preferences: raw.preferences === "yes",
    };
  } catch {
    return null;
  }
}
